import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BookingPanel = ({ event, selectedTiers, onDiscountApply }) => {
  const navigate = useNavigate();
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  // Mock discount codes
  const validDiscounts = {
    'EARLY20': { type: 'percentage', value: 20, description: '20% Early Bird Discount' },
    'SAVE10': { type: 'percentage', value: 10, description: '10% Off' },
    'STUDENT': { type: 'percentage', value: 15, description: '15% Student Discount' },
    'FIRST25': { type: 'fixed', value: 25, description: '$25 Off First Event' }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let fees = 0;
    let ticketCount = 0;

    Object.entries(selectedTiers)?.forEach(([tierId, quantity]) => {
      if (quantity > 0) {
        const tier = event?.tiers?.find(t => t?.id === tierId);
        if (tier) {
          subtotal += tier?.price * quantity;
          fees += tier?.fees * quantity;
          ticketCount += quantity;
        }
      }
    });

    let discount = 0;
    if (appliedDiscount) {
      if (appliedDiscount?.type === 'percentage') {
        discount = subtotal * (appliedDiscount?.value / 100);
      } else {
        discount = Math.min(appliedDiscount?.value, subtotal);
      }
    }

    const total = subtotal + fees - discount;

    return {
      subtotal,
      fees,
      discount,
      total: Math.max(0, total),
      ticketCount
    };
  };

  const handleApplyDiscount = async () => {
    if (!discountCode?.trim()) return;

    setIsApplyingDiscount(true);
    setDiscountError('');

    // Simulate API call
    setTimeout(() => {
      let discount = validDiscounts?.[discountCode?.toUpperCase()];
      if (discount) {
        setAppliedDiscount(discount);
        onDiscountApply && onDiscountApply(discount);
        setDiscountError('');
      } else {
        setDiscountError('Invalid discount code');
        setAppliedDiscount(null);
      }
      setIsApplyingDiscount(false);
    }, 1000);
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
    onDiscountApply && onDiscountApply(null);
  };

  const handleProceedToBooking = () => {
    const totals = calculateTotals();
    const bookingData = {
      eventId: event?.id,
      selectedTiers,
      totals,
      appliedDiscount,
      event: {
        title: event?.title,
        date: event?.date,
        time: event?.time,
        venue: event?.venue
      }
    };
    
    // Store booking data in sessionStorage for the booking flow
    sessionStorage.setItem('bookingData', JSON.stringify(bookingData));
    navigate('/event-booking-flow');
  };

  const totals = calculateTotals();
  const hasSelectedTickets = totals?.ticketCount > 0;

  return (
    <div className="sticky top-24 space-y-6">
      {/* Quick Event Info */}
      <div className="p-6 bg-card border border-border rounded-lg">
        <h3 className="font-semibold text-foreground mb-4">Booking Summary</h3>
        
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-muted-foreground">{event?.date}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="Clock" size={16} className="text-primary" />
            <span className="text-muted-foreground">{event?.time}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="MapPin" size={16} className="text-primary" />
            <span className="text-muted-foreground">{event?.venue?.name}</span>
          </div>
        </div>

        {/* Selected Tickets Summary */}
        {hasSelectedTickets && (
          <div className="border-t border-border pt-4 mb-4">
            <h4 className="font-medium text-foreground mb-3">Selected Tickets</h4>
            <div className="space-y-2">
              {Object.entries(selectedTiers)?.map(([tierId, quantity]) => {
                if (quantity === 0) return null;
                const tier = event?.tiers?.find(t => t?.id === tierId);
                if (!tier) return null;

                return (
                  <div key={tierId} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {tier?.name} × {quantity}
                    </span>
                    <span className="font-medium text-foreground">
                      ${(tier?.price * quantity)?.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Discount Code */}
        <div className="border-t border-border pt-4 mb-4">
          <h4 className="font-medium text-foreground mb-3">Discount Code</h4>
          
          {!appliedDiscount ? (
            <div className="space-y-2">
              <div className="flex space-x-2">
                <Input
                  type="text"
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e?.target?.value?.toUpperCase())}
                  className="flex-1"
                  error={discountError}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApplyDiscount}
                  disabled={!discountCode?.trim() || isApplyingDiscount}
                  loading={isApplyingDiscount}
                >
                  Apply
                </Button>
              </div>
              
              {/* Sample Codes Hint */}
              <div className="text-xs text-muted-foreground">
                Try: EARLY20, SAVE10, STUDENT, FIRST25
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-lg">
              <div>
                <div className="text-sm font-medium text-success">
                  {appliedDiscount?.description}
                </div>
                <div className="text-xs text-muted-foreground">
                  Code: {discountCode}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveDiscount}
              >
                <Icon name="X" size={14} />
              </Button>
            </div>
          )}
        </div>

        {/* Price Breakdown */}
        {hasSelectedTickets && (
          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">${totals?.subtotal?.toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fees</span>
              <span className="text-foreground">${totals?.fees?.toFixed(2)}</span>
            </div>
            
            {totals?.discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-success">Discount</span>
                <span className="text-success">-${totals?.discount?.toFixed(2)}</span>
              </div>
            )}
            
            <div className="border-t border-border pt-2 flex justify-between font-semibold">
              <span className="text-foreground">Total</span>
              <span className="text-foreground text-lg">${totals?.total?.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 space-y-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            disabled={!hasSelectedTickets}
            onClick={handleProceedToBooking}
            iconName="CreditCard"
            iconPosition="left"
          >
            {hasSelectedTickets 
              ? `Book ${totals?.ticketCount} Ticket${totals?.ticketCount > 1 ? 's' : ''}`
              : 'Select Tickets to Continue'
            }
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            fullWidth
            iconName="Heart"
            iconPosition="left"
          >
            Save to Wishlist
          </Button>
        </div>

        {/* Security Badge */}
        <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} className="text-success" />
          <span>Secure checkout with 256-bit SSL encryption</span>
        </div>
      </div>
      {/* Event Stats */}
      <div className="p-4 bg-muted rounded-lg">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-foreground">
              {event?.totalCapacity - event?.bookedSeats}
            </div>
            <div className="text-xs text-muted-foreground">Spots Left</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-foreground">
              {event?.attendeeCount}
            </div>
            <div className="text-xs text-muted-foreground">Going</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPanel;